/**
 * The styles of one asset (spec `docs/superpowers/specs/2026-10-05-music24-release-themes-design.md`
 * §4.4). A style is the technique of ONE asset: what moves and how the layout works. The user
 * picks it in the Studio when the asset is made (`params.style`, and `params.waveform` for the
 * shape of the Waveform style). The theme of the song (`releaseThemes.ts`) still sets the colours,
 * the fonts, the texture, the pace and the text animation.
 *
 * The values are the approved design (`release-themes.values.json`: `styles`, `assetTypes`,
 * `themes[].defaultStyles`). Change a style, the styles of an asset type or a theme default here,
 * nowhere else. The file imports only types, so every project can use it.
 */
import type { ReleaseThemeId, ThemeArtworkMotion } from './releaseThemes';
import type { StudioThemeId } from './studioThemes';

export const ASSET_STYLE_IDS = [
  // images (and the countdown)
  'coverCard',
  'coverGround',
  'typeOnly',
  // loops
  'drift',
  'pulse',
  'liquid',
  'waveform',
  'particles',
  'aiFootage',
] as const;
export type AssetStyleId = (typeof ASSET_STYLE_IDS)[number];

/**
 * The shape that moves with the song: of the Waveform style of a kit asset (`bars`, `line`,
 * `ring`), and of an AI reel (also `none`: the reel has no waveform).
 */
export const WAVEFORM_SHAPES = ['bars', 'line', 'ring', 'none'] as const;
export type WaveformShape = (typeof WAVEFORM_SHAPES)[number];

/** Where the picture of the segments comes from. `ground` is the ground of the theme with no picture. */
export type AssetStyleSource = 'artwork' | 'bankClip' | 'ground';
/** How the cover is drawn: over the full frame, as a card in the frame of the theme, or blurred as a ground. */
export type AssetStyleArtworkLayout = 'full' | 'card' | 'blurred';
/** The layer that moves with the song: none, bars, particles, or the shape of `params.waveform`. */
export type AssetStyleReactive = 'none' | 'bars' | 'particles' | 'waveform';
/**
 * The cover on a wide (16:9) frame, where a square cover never fills the frame (spec §4.2): `card` =
 * a card in the frame of the theme on the ground of the theme; `cardOnBlur` = the same card on the
 * blurred cover. Read it with `wideArtworkLayoutOf`.
 */
export type WideArtworkLayout = 'card' | 'cardOnBlur';

export interface AssetStyle {
  id: AssetStyleId;
  /** The English name. The UI shows a react-intl message, not this text. */
  label: string;
  /** What the style makes: the binding instruction for the director. */
  directorHint: string;
  /** What the plan check and the timeline builder enforce. */
  mechanic: {
    source: AssetStyleSource;
    artworkLayout: AssetStyleArtworkLayout;
    /** The cover motion of a video. Null: the theme decides (`brief.artworkMotions`). An image is still. */
    artworkMotion: ThemeArtworkMotion | null;
    reactive: AssetStyleReactive;
    /**
     * The cover on a 16:9 frame. Null: the style has no card there (`artworkLayout` applies: a
     * blurred ground, type only, footage). The Waveform style depends on its shape: use
     * `wideArtworkLayoutOf`, which `buildAssetTimeline` reads when the shape is 16:9.
     */
    wideArtworkLayout: WideArtworkLayout | null;
  };
}

export const ASSET_STYLES: Readonly<Record<AssetStyleId, AssetStyle>> = {
  coverCard: {
    id: 'coverCard',
    label: 'Cover card',
    directorHint: 'An image. The cover is a card in the frame of the theme, on the ground of the theme.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'card',
      artworkMotion: null,
      reactive: 'none',
      wideArtworkLayout: 'card',
    },
  },
  coverGround: {
    id: 'coverGround',
    label: 'Cover ground',
    directorHint: 'An image. The cover is blurred and fills the frame, under a scrim in the ground colour. Large type.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'blurred',
      artworkMotion: 'still',
      reactive: 'none',
      wideArtworkLayout: null,
    },
  },
  typeOnly: {
    id: 'typeOnly',
    label: 'Type only',
    directorHint: 'Large words on the ground of the theme. No cover and no footage.',
    mechanic: {
      source: 'ground',
      artworkLayout: 'full',
      artworkMotion: 'still',
      reactive: 'none',
      wideArtworkLayout: null,
    },
  },
  drift: {
    id: 'drift',
    label: 'Cover drift',
    directorHint: 'The cover fills the frame and moves slowly.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'full',
      artworkMotion: 'drift',
      reactive: 'none',
      wideArtworkLayout: 'cardOnBlur',
    },
  },
  pulse: {
    id: 'pulse',
    label: 'Pulse',
    directorHint: 'The cover and bars move on the beat.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'full',
      artworkMotion: 'pulse',
      reactive: 'bars',
      wideArtworkLayout: 'card',
    },
  },
  liquid: {
    id: 'liquid',
    label: 'Liquid',
    directorHint: 'The colours of the theme flow like ink.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'full',
      artworkMotion: 'liquid',
      reactive: 'none',
      wideArtworkLayout: null,
    },
  },
  waveform: {
    id: 'waveform',
    label: 'Waveform',
    directorHint: 'The cover and one shape that moves with the song. Shape: bars, line or ring.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'full',
      artworkMotion: null,
      reactive: 'waveform',
      wideArtworkLayout: 'cardOnBlur',
    },
  },
  particles: {
    id: 'particles',
    label: 'Particles',
    directorHint: 'Dots in the accent that rise with the song.',
    mechanic: {
      source: 'artwork',
      artworkLayout: 'full',
      artworkMotion: null,
      reactive: 'particles',
      wideArtworkLayout: 'cardOnBlur',
    },
  },
  aiFootage: {
    id: 'aiFootage',
    label: 'AI footage',
    directorHint: 'Bank clips of the footage moods of the theme, cut at the pace of the theme.',
    mechanic: {
      source: 'bankClip',
      artworkLayout: 'full',
      artworkMotion: null,
      reactive: 'none',
      wideArtworkLayout: null,
    },
  },
};

/** The styles and the waveform shapes that one asset type offers. */
export interface AssetTypeStyles {
  /** In menu order. Empty: the asset type has no style choice (the cover sizes are a plain resize). */
  styles: readonly AssetStyleId[];
  /** The shapes of the Waveform style (a kit asset), or the waveform option of the AI reel. Empty: none. */
  waveforms: readonly WaveformShape[];
}

/**
 * The lists of the design: the phase 1 asset types (design q7) and the full videos, the lyric
 * moment and the motion artwork (design s6).
 */
const DESIGN_ASSET_TYPES = {
  story: { styles: ['coverCard', 'coverGround', 'typeOnly'], waveforms: [] },
  feedPost: { styles: ['coverCard', 'coverGround', 'typeOnly'], waveforms: [] },
  banners: { styles: ['coverCard', 'coverGround', 'typeOnly'], waveforms: [] },
  // The picture is its own choice (`params.headerSource`, `spotifyHeader.ts`), not a style.
  spotifyHeader: { styles: [], waveforms: [] },
  thumbnail: { styles: ['coverCard', 'coverGround'], waveforms: [] },
  linkPreview: { styles: ['coverCard', 'coverGround', 'typeOnly'], waveforms: [] },
  lyricCard: { styles: ['coverGround', 'typeOnly'], waveforms: [] },
  coverSizes: { styles: [], waveforms: [] },
  canvasLoop: {
    styles: ['drift', 'pulse', 'liquid', 'waveform', 'particles', 'aiFootage'],
    waveforms: ['bars', 'line', 'ring'],
  },
  artworkInMotion: {
    styles: ['drift', 'pulse', 'liquid', 'waveform', 'particles', 'aiFootage'],
    waveforms: ['bars', 'line', 'ring'],
  },
  // Design o (owner decision 2026-10-08): the countdown always shows the cover, so no Type only.
  countdown: { styles: ['coverCard', 'pulse'], waveforms: [] },
  aiReel: { styles: ['aiFootage'], waveforms: ['bars', 'line', 'ring', 'none'] },
  // No ring in any theme (design z2, owner answer 1): it cropped the cover to a circle.
  visualizer: { styles: ['waveform', 'particles', 'pulse', 'drift'], waveforms: ['bars', 'line'] },
  lyricVideo: { styles: ['coverGround', 'coverCard', 'typeOnly', 'aiFootage'], waveforms: [] },
  musicVideo: { styles: ['aiFootage'], waveforms: [] },
  lyricMoment: { styles: ['coverGround', 'typeOnly', 'aiFootage'], waveforms: [] },
  // The Liquid ink tinted the whole cover; Apple wants the cover art itself (design w, owner default 2026-10-08).
  motionArtwork: { styles: ['drift'], waveforms: [] },
} satisfies Record<StudioThemeId, AssetTypeStyles>;

/** The styles of every asset type. */
export const ASSET_TYPE_STYLES: Readonly<Record<StudioThemeId, AssetTypeStyles>> = DESIGN_ASSET_TYPES;

/** A style choice: the style, and the waveform shape when the style or the asset type has one. */
export interface AssetStyleChoice {
  style: AssetStyleId;
  waveform?: WaveformShape;
}

/**
 * The default style of each theme per asset type, so "Make the kit" needs no style choice. An
 * asset type that a theme does not name uses the first style of `ASSET_TYPE_STYLES`. No default
 * of a kit asset is `aiFootage`: a default never needs `canUseAiReels` (the test checks it). The
 * exceptions are the AI reel and the music video: AI footage is their only style. The lyric video
 * and the lyric moment take the lyric card default of the theme (design s6).
 */
export const THEME_DEFAULT_STYLES: Readonly<
  Record<ReleaseThemeId, Readonly<Partial<Record<StudioThemeId, AssetStyleChoice>>>>
> = {
  clean: {
    story: { style: 'coverCard' },
    feedPost: { style: 'coverCard' },
    banners: { style: 'coverCard' },
    linkPreview: { style: 'coverCard' },
    thumbnail: { style: 'coverCard' },
    lyricCard: { style: 'coverGround' },
    canvasLoop: { style: 'drift' },
    artworkInMotion: { style: 'drift' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'bars' },
    visualizer: { style: 'waveform', waveform: 'bars' },
    lyricVideo: { style: 'coverGround' },
    lyricMoment: { style: 'coverGround' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  hype: {
    story: { style: 'typeOnly' },
    feedPost: { style: 'typeOnly' },
    banners: { style: 'typeOnly' },
    linkPreview: { style: 'typeOnly' },
    thumbnail: { style: 'coverGround' },
    lyricCard: { style: 'typeOnly' },
    canvasLoop: { style: 'pulse' },
    artworkInMotion: { style: 'pulse' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'bars' },
    visualizer: { style: 'pulse' },
    lyricVideo: { style: 'typeOnly' },
    lyricMoment: { style: 'typeOnly' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  dreamy: {
    story: { style: 'coverGround' },
    feedPost: { style: 'coverGround' },
    banners: { style: 'coverGround' },
    linkPreview: { style: 'coverGround' },
    thumbnail: { style: 'coverGround' },
    lyricCard: { style: 'coverGround' },
    canvasLoop: { style: 'liquid' },
    artworkInMotion: { style: 'liquid' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'line' },
    visualizer: { style: 'particles' },
    lyricVideo: { style: 'coverGround' },
    lyricMoment: { style: 'coverGround' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  dark: {
    story: { style: 'coverCard' },
    feedPost: { style: 'coverCard' },
    banners: { style: 'coverCard' },
    linkPreview: { style: 'coverCard' },
    thumbnail: { style: 'coverCard' },
    lyricCard: { style: 'typeOnly' },
    canvasLoop: { style: 'drift' },
    artworkInMotion: { style: 'drift' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'ring' },
    visualizer: { style: 'waveform', waveform: 'bars' },
    lyricVideo: { style: 'typeOnly' },
    lyricMoment: { style: 'typeOnly' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  warm: {
    story: { style: 'coverCard' },
    feedPost: { style: 'coverCard' },
    banners: { style: 'coverCard' },
    linkPreview: { style: 'coverCard' },
    thumbnail: { style: 'coverCard' },
    lyricCard: { style: 'coverGround' },
    canvasLoop: { style: 'drift' },
    artworkInMotion: { style: 'drift' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'line' },
    visualizer: { style: 'particles' },
    lyricVideo: { style: 'coverGround' },
    lyricMoment: { style: 'coverGround' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  retro: {
    story: { style: 'coverCard' },
    feedPost: { style: 'coverCard' },
    banners: { style: 'coverCard' },
    linkPreview: { style: 'coverCard' },
    thumbnail: { style: 'coverCard' },
    lyricCard: { style: 'typeOnly' },
    canvasLoop: { style: 'waveform', waveform: 'bars' },
    artworkInMotion: { style: 'waveform', waveform: 'bars' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'bars' },
    visualizer: { style: 'waveform', waveform: 'bars' },
    lyricVideo: { style: 'typeOnly' },
    lyricMoment: { style: 'typeOnly' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  neon: {
    story: { style: 'typeOnly' },
    feedPost: { style: 'typeOnly' },
    banners: { style: 'typeOnly' },
    linkPreview: { style: 'typeOnly' },
    thumbnail: { style: 'coverGround' },
    lyricCard: { style: 'typeOnly' },
    canvasLoop: { style: 'waveform', waveform: 'ring' },
    artworkInMotion: { style: 'waveform', waveform: 'ring' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'ring' },
    visualizer: { style: 'waveform', waveform: 'bars' },
    lyricVideo: { style: 'typeOnly' },
    lyricMoment: { style: 'typeOnly' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
  handmade: {
    story: { style: 'coverCard' },
    feedPost: { style: 'coverCard' },
    banners: { style: 'coverCard' },
    linkPreview: { style: 'coverCard' },
    thumbnail: { style: 'coverCard' },
    lyricCard: { style: 'coverGround' },
    canvasLoop: { style: 'drift' },
    artworkInMotion: { style: 'drift' },
    countdown: { style: 'coverCard' },
    aiReel: { style: 'aiFootage', waveform: 'line' },
    visualizer: { style: 'waveform', waveform: 'line' },
    lyricVideo: { style: 'coverGround' },
    lyricMoment: { style: 'coverGround' },
    motionArtwork: { style: 'drift' },
    musicVideo: { style: 'aiFootage' },
  },
};

export function isAssetStyleId(value: unknown): value is AssetStyleId {
  return typeof value === 'string' && (ASSET_STYLE_IDS as readonly string[]).includes(value);
}

export function isWaveformShape(value: unknown): value is WaveformShape {
  return typeof value === 'string' && (WAVEFORM_SHAPES as readonly string[]).includes(value);
}

/** The style row of an id. */
export function assetStyleById(id: AssetStyleId): AssetStyle {
  return ASSET_STYLES[id];
}

/** The styles that an asset type offers, in menu order. */
export function stylesOfAssetType(assetTypeId: StudioThemeId): readonly AssetStyleId[] {
  return ASSET_TYPE_STYLES[assetTypeId].styles;
}

/** The waveform shapes that an asset type offers. */
export function waveformsOfAssetType(assetTypeId: StudioThemeId): readonly WaveformShape[] {
  return ASSET_TYPE_STYLES[assetTypeId].waveforms;
}

/** True when the asset type offers the style. */
export function assetTypeOffersStyle(assetTypeId: StudioThemeId, style: AssetStyleId): boolean {
  return ASSET_TYPE_STYLES[assetTypeId].styles.includes(style);
}

/** True when the asset type offers the waveform shape. */
export function assetTypeOffersWaveform(assetTypeId: StudioThemeId, waveform: WaveformShape): boolean {
  return ASSET_TYPE_STYLES[assetTypeId].waveforms.includes(waveform);
}

/** True when a style of the asset type takes a waveform shape: the Waveform style, and the AI reel. */
export function styleTakesWaveform(assetTypeId: StudioThemeId, style: AssetStyleId): boolean {
  return ASSET_TYPE_STYLES[assetTypeId].waveforms.length > 0 && (style === 'waveform' || assetTypeId === 'aiReel');
}

/**
 * The style of an asset with no style choice: the default that the theme names for the asset type,
 * else the first style of the asset type. Null for an asset type with no styles.
 */
export function defaultStyleFor(themeId: ReleaseThemeId, assetTypeId: StudioThemeId): AssetStyleChoice | null {
  const offered = ASSET_TYPE_STYLES[assetTypeId].styles;
  const named = THEME_DEFAULT_STYLES[themeId][assetTypeId];
  if (named !== undefined && offered.includes(named.style)) {
    return { ...named };
  }
  const first = offered[0];
  return first === undefined ? null : { style: first };
}

/** The style of an asset as it is made: `waveform` is null when the style takes no shape. */
export interface ResolvedAssetStyle {
  style: AssetStyleId;
  waveform: WaveformShape | null;
}

/**
 * The style of a stored asset or of a request (`params.style`, `params.waveform`). A style that
 * the asset type offers stays; every other value (an old row has none) gives the default of the
 * theme. The waveform shape is the stored one when the asset type offers it, else the shape of the
 * theme default for this asset type, else the reel waveform of the theme, else the first shape of
 * the asset type. Null for an asset type with no styles. Pure.
 */
export function resolveAssetStyle(
  themeId: ReleaseThemeId,
  assetTypeId: StudioThemeId,
  stored: { style?: unknown; waveform?: unknown }
): ResolvedAssetStyle | null {
  const fallback = defaultStyleFor(themeId, assetTypeId);
  if (fallback === null) {
    return null;
  }
  const style =
    isAssetStyleId(stored.style) && assetTypeOffersStyle(assetTypeId, stored.style) ? stored.style : fallback.style;
  if (!styleTakesWaveform(assetTypeId, style)) {
    return { style, waveform: null };
  }
  const offered = ASSET_TYPE_STYLES[assetTypeId].waveforms;
  const candidates = [
    stored.waveform,
    fallback.style === style ? fallback.waveform : undefined,
    THEME_DEFAULT_STYLES[themeId].aiReel?.waveform,
    offered[0],
  ];
  const waveform = candidates.find(
    (candidate): candidate is WaveformShape => isWaveformShape(candidate) && offered.includes(candidate)
  );
  return { style, waveform: waveform ?? null };
}

/**
 * The cover on a wide (16:9) frame for a style and its waveform shape (spec §4.2): the card on the
 * blurred cover for drift, particles and the waveform bars; the card on the ground of the theme for
 * pulse, the waveform line and ring (the ring is drawn around the centred card) and the cover card.
 * Null for a style with no card on a wide frame. Pure.
 */
export function wideArtworkLayoutOf(style: AssetStyleId, waveform: WaveformShape | null): WideArtworkLayout | null {
  if (style === 'waveform' && (waveform === 'line' || waveform === 'ring')) {
    return 'card';
  }
  return ASSET_STYLES[style].mechanic.wideArtworkLayout;
}

/** The frame shapes of a kit asset (a twin of `KitShape` in `studioThemes.ts`). */
type CardShape = '9:16' | '1:1' | '4:5' | '16:9' | '3:1' | '1.91:1';

const SHAPE_RATIO: Readonly<Record<CardShape, number>> = {
  '9:16': 9 / 16,
  '1:1': 1,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
  '3:1': 3,
  '1.91:1': 1.91,
};

/**
 * The box of the cover card (`coverCard`), in shares of the frame. The card is a square in pixels.
 * A tall or square frame has it in the upper centre, a wide frame has it on the left. The director
 * gets the box and keeps the text off it; the renderer draws the cover in it, in the frame of the
 * theme. Pure.
 */
export function coverCardBox(shape: CardShape): { x: number; y: number; w: number; h: number } {
  const ratio = SHAPE_RATIO[shape];
  if (ratio > 1.2) {
    // Wide: the card takes 64 % of the height, on the left.
    const h = 0.64;
    const w = h / ratio;
    return { x: 0.06, y: (1 - h) / 2, w, h };
  }
  // Tall or square: the card takes 56 % of the width, in the upper centre (the design tile).
  const w = 0.56;
  const h = w * ratio;
  return { x: (1 - w) / 2, y: ratio < 0.7 ? 0.2 : 0.1, w, h };
}
